import { memo, useCallback, useRef } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import type { Attachment } from '@travelity/api/services/customer/customer.types';
import StepContent from '@/views/add-product/components/step-content';
import type { PackageFormValues } from '../../add-package.types';
import UploadedImagesGrid from './UploadedImagesGrid';
import {
    fileToBase64,
    getAttachmentName,
    getImageSize,
} from './helpers/assets.step.helpers';

const AssetsStep = ({ errors }: { errors: string[] }) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const { setValue } = useFormContext<PackageFormValues>();
    const images: Attachment[] = useWatch({ name: 'assets.images' }) || [];

    const handleFiles = useCallback(
        async (files: FileList | null) => {
            if (!files?.length) return;
            const existingNames = new Set(
                images.map(image =>
                    getAttachmentName(image).trim().toLowerCase()
                )
            );
            const fileList = Array.from(files).filter(file => {
                if (!file.type.startsWith('image/')) return false;
                const key = file.name.trim().toLowerCase();
                return !existingNames.has(key);
            });
            if (!fileList.length) return;
            const newImages = await Promise.all(
                fileList.map(async file => {
                    const [content, size] = await Promise.all([
                        fileToBase64(file),
                        getImageSize(file),
                    ]);
                    return {
                        name: file.name,
                        contentType: file.type,
                        content,
                        width: size.width,
                        height: size.height,
                    } satisfies Attachment;
                })
            );
            const next = [...images, ...newImages];
            setValue('assets.images', next, { shouldDirty: true });
        },
        [images, setValue]
    );

    const handleImagesChange = useCallback(
        (next: Attachment[]) => {
            setValue('assets.images', next, { shouldDirty: true });
        },
        [setValue]
    );

    const handleRemoveAll = useCallback(() => {
        setValue('assets.images', [], { shouldDirty: true });
    }, [setValue]);

    return (
        <StepContent title="Assets" errors={errors}>
            <Stack spacing={2} sx={{ maxWidth: 640 }}>
                <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Upload package images
                    </Typography>
                </Box>
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center' }}
                >
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        hidden
                        onChange={e => {
                            handleFiles(e.target.files);
                            e.target.value = '';
                        }}
                    />
                    <Button
                        variant="outlined"
                        onClick={() => inputRef.current?.click()}
                        sx={{ width: 'fit-content' }}
                    >
                        Choose images
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleRemoveAll}
                        disabled={!images.length}
                        aria-label="Remove all images"
                    >
                        Remove all
                    </Button>
                </Stack>

                <UploadedImagesGrid
                    images={images}
                    onChange={handleImagesChange}
                />
            </Stack>
        </StepContent>
    );
};

export default memo(AssetsStep);
